import type { components } from "../../../api/generated-schema";
import { createRequestId, ApiProblem } from "../../../api/httpClient";
import { getAuthenticationSession } from "../../../auth/session";

export type MemorialEntryAdminRow = components["schemas"]["MemorialEntryAdminRow"];
export type MemorialEntryAdminListResponse = components["schemas"]["MemorialEntryAdminListResponse"];
export type MemorialEntryWithdrawRequest = components["schemas"]["MemorialEntryWithdrawRequest"];
export type MemorialEntryWithdrawResponse = components["schemas"]["MemorialEntryWithdrawResponse"];

export type ListMemorialAdminParams = Readonly<{
  cemeteryId?: string;
  status?: string;
  cursor?: string;
  size?: number;
}>;

export async function listMemorialEntriesAdmin(
  params: ListMemorialAdminParams = {},
  signal?: AbortSignal,
): Promise<MemorialEntryAdminListResponse> {
  const query = new URLSearchParams();
  if (params.cemeteryId) query.set("cemeteryId", params.cemeteryId);
  if (params.status) query.set("status", params.status);
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.size) query.set("size", String(params.size));

  const auth = getAuthenticationSession();
  const token = await auth.getAccessToken();
  const queryString = query.toString();
  const url = `/api/v1/memorial-entries${queryString ? `?${queryString}` : ""}`;

  const init: RequestInit = {
    method: "GET",
    headers: {
      Accept: "application/json, application/problem+json",
      Authorization: `Bearer ${token}`,
      "X-Request-Id": createRequestId(),
    },
  };
  if (signal) {
    init.signal = signal;
  }

  const response = await fetch(url, init);

  if (!response.ok) {
    const rawProblem = (await response.json().catch(() => null)) as
      | components["schemas"]["ProblemDetail"]
      | null;
    const problem: components["schemas"]["ProblemDetail"] = rawProblem ?? {
      type: "urn:ncc:error:unknown",
      title: "Lỗi hệ thống",
      status: response.status,
      code: "COMMON_UNKNOWN_ERROR",
      detail: "Đã xảy ra lỗi khi gửi yêu cầu",
      instance: url,
      requestId: createRequestId(),
      timestamp: new Date().toISOString(),
    };
    throw new ApiProblem(response.status, problem);
  }

  return response.json() as Promise<MemorialEntryAdminListResponse>;
}

export async function withdrawMemorialEntry(
  memorialEntryId: string,
  reason: string,
  signal?: AbortSignal,
): Promise<MemorialEntryWithdrawResponse> {
  const auth = getAuthenticationSession();
  const token = await auth.getAccessToken();
  const url = `/api/v1/memorial-entries/${encodeURIComponent(memorialEntryId)}/withdraw`;

  const init: RequestInit = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json, application/problem+json",
      Authorization: `Bearer ${token}`,
      "X-Request-Id": createRequestId(),
    },
    body: JSON.stringify({ reason }),
  };
  if (signal) {
    init.signal = signal;
  }

  const response = await fetch(url, init);

  if (!response.ok) {
    const rawProblem = (await response.json().catch(() => null)) as
      | components["schemas"]["ProblemDetail"]
      | null;
    const problem: components["schemas"]["ProblemDetail"] = rawProblem ?? {
      type: "urn:ncc:error:unknown",
      title: "Lỗi hệ thống",
      status: response.status,
      code: "COMMON_UNKNOWN_ERROR",
      detail: "Đã xảy ra lỗi khi gửi yêu cầu",
      instance: url,
      requestId: createRequestId(),
      timestamp: new Date().toISOString(),
    };
    throw new ApiProblem(response.status, problem);
  }

  return response.json() as Promise<MemorialEntryWithdrawResponse>;
}
